import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import AuthPage from './AuthPage';
import CreateTeamPage from './CreateTeamPage';
import TeamCourtPage from './TeamCourtPage';
import MyTeamCompetitionPage from './MyTeamCompetitionPage';
import LeaguesPage from './components/LeaguesPage';
import LeagueRankingPage from './components/LeagueRankingPage';
import FechasPage from './components/FechasPage';
import RulesPage from './components/RulesPage';
import EditProfilePage from './components/EditProfilePage';
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import AppEventBus from './services/AppEventBus';

// Componente wrapper para rutas que requieren Navbar
const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="min-h-screen bg-[#001A3E]">
      <Navbar />
      {/* pb-20 en mobile para dejar espacio a la bottom nav */}
      <main className="container mx-auto px-4 py-6 pb-24 md:pb-8 max-w-5xl">
        {children}
      </main>
    </div>
  );
};

// Wrapper para LeaguesPage que maneja la navegación
const LeaguesPageWrapper: React.FC = () => {
  const navigate = useNavigate();
  
  const handleViewRanking = (leagueId: string, leagueName: string) => {
    navigate(`/Ligas/${leagueId}`, { 
      state: { leagueName } 
    });
  };

  return <LeaguesPage onViewRanking={handleViewRanking} />;
};

// Wrapper para FechasPage que maneja parámetros de URL
const FechasPageWrapper: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  
  // Leer parámetros de la URL
  const matchdayId = searchParams.get('matchdayId') || undefined;
  const teamId = searchParams.get('teamId') || undefined;
  const teamName = searchParams.get('teamName') ? decodeURIComponent(searchParams.get('teamName')!) : undefined;
  
  // Si venimos de LeagueRankingPage, el onBack debería volver ahí
  const fromLeagueRanking = location.state?.fromLeagueRanking;
  const leagueId = location.state?.leagueId;
  
  const handleBack = () => {
    if (fromLeagueRanking && leagueId) {
      navigate(`/Ligas/${leagueId}`);
    } else {
      navigate('/MiEquipo');
    }
  };

  // Manejar navegación desde AppEventBus
  useEffect(() => {
    const handleNavigateFechas = (payload?: { matchdayId?: string }) => {
      const newSearchParams = new URLSearchParams(location.search);
      if (payload?.matchdayId) {
        newSearchParams.set('matchdayId', payload.matchdayId);
      }
      navigate(`/Fechas?${newSearchParams.toString()}`);
    };

    AppEventBus.on('navigate-fechas', handleNavigateFechas);
    return () => {
      AppEventBus.off('navigate-fechas', handleNavigateFechas);
    };
  }, [location.search, navigate]);

  return (
    <FechasPage
      initialMatchdayId={matchdayId}
      targetTeamId={teamId}
      targetTeamName={teamName}
      onBack={teamId ? handleBack : undefined}
    />
  );
};

// Wrapper para CreateTeamPage que maneja navegación después de crear equipo
const CreateTeamPageWrapper: React.FC = () => {
  const navigate = useNavigate();

  const handleTeamCreated = () => {
    navigate('/ConfigurarEquipo', { replace: true });
  };

  return <CreateTeamPage onTeamCreated={handleTeamCreated} />;
};

// Wrapper para TeamCourtPage que maneja navegación después de confirmar equipo
const TeamCourtPageWrapper: React.FC = () => {
  const navigate = useNavigate();

  const handleTeamConfirmed = () => {
    navigate('/MiEquipo', { replace: true });
  };

  return <TeamCourtPage onTeamConfirmed={handleTeamConfirmed} />;
};

// Wrapper para EditProfilePage que maneja navegación
const EditProfilePageWrapper: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/MiEquipo');
  };

  return <EditProfilePage onBack={handleBack} />;
};

// Actualizar LeagueRankingPageWrapper para pasar el estado correcto a FechasPage
const LeagueRankingPageWrapperUpdated: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { leagueId } = useParams<{ leagueId: string }>();
  const leagueName = (location.state as any)?.leagueName || 'Liga';

  const handleBack = () => {
    navigate('/Ligas');
  };

  const handleViewParticipantFechas = (teamId: string, teamName: string) => {
    navigate(`/Fechas?teamId=${teamId}&teamName=${encodeURIComponent(teamName)}`, {
      state: { fromLeagueRanking: true, leagueId }
    });
  };

  if (!leagueId) {
    return <Navigate to="/Ligas" replace />;
  }

  return (
    <LeagueRankingPage
      leagueId={leagueId}
      leagueName={leagueName}
      onBack={handleBack}
      onViewParticipantFechas={handleViewParticipantFechas}
    />
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Rutas públicas */}
        <Route path="/auth" element={<AuthPage />} />
        
        {/* Rutas protegidas que requieren autenticación */}
        <Route
          path="/create-team"
          element={
            <ProtectedRoute>
              <CreateTeamPageWrapper />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ConfigurarEquipo"
          element={
            <ProtectedRoute requiresTeam={true}>
              <TeamCourtPageWrapper />
            </ProtectedRoute>
          }
        />

        {/* Rutas protegidas que requieren equipo confirmado y muestran Navbar */}
        <Route
          path="/MiEquipo"
          element={
            <ProtectedRoute requiresConfirmedTeam={true}>
              <AppLayout>
                <MyTeamCompetitionPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Mantener / como alias de /MiEquipo para compatibilidad */}
        <Route
          path="/"
          element={
            <ProtectedRoute requiresConfirmedTeam={true}>
              <AppLayout>
                <MyTeamCompetitionPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/Ligas"
          element={
            <ProtectedRoute requiresConfirmedTeam={true}>
              <AppLayout>
                <LeaguesPageWrapper />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/Ligas/:leagueId"
          element={
            <ProtectedRoute requiresConfirmedTeam={true}>
              <AppLayout>
                <LeagueRankingPageWrapperUpdated />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/Fechas"
          element={
            <ProtectedRoute requiresConfirmedTeam={true}>
              <AppLayout>
                <FechasPageWrapper />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/Reglamento"
          element={
            <ProtectedRoute requiresConfirmedTeam={true}>
              <AppLayout>
                <RulesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/Perfil"
          element={
            <ProtectedRoute requiresConfirmedTeam={true}>
              <AppLayout>
                <EditProfilePageWrapper />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Redirigir rutas antiguas o no encontradas */}
        <Route path="/team" element={<Navigate to="/MiEquipo" replace />} />
        <Route path="/team-setup" element={<Navigate to="/ConfigurarEquipo" replace />} />
        <Route path="/configurar-equipo" element={<Navigate to="/ConfigurarEquipo" replace />} />
        <Route path="/rules" element={<Navigate to="/Reglamento" replace />} />
        <Route path="/reglamento" element={<Navigate to="/Reglamento" replace />} />
        <Route path="/profile" element={<Navigate to="/Perfil" replace />} />
        <Route path="/perfil" element={<Navigate to="/Perfil" replace />} />
        <Route path="/matchdays" element={<Navigate to="/MiEquipo" replace />} />
        <Route path="/miEquipo" element={<Navigate to="/MiEquipo" replace />} />
        <Route path="/fechas" element={<Navigate to="/Fechas" replace />} />
        <Route path="/ligas" element={<Navigate to="/Ligas" replace />} />
        
        {/* 404 - redirigir a la página principal si el usuario está autenticado, sino a auth */}
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <Navigate to="/MiEquipo" replace />
            </ProtectedRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
